import { useLoadMore } from '~/hooks/useLoadMore';
import {
  search,
  SearchParams,
  SearchType,
} from '~/redux/api/methods/evisearch';

import { flattenSearchSelectedItemValues } from '../helpers/NodeEnricher/flattenSearchSelectedItemValues';

export interface SearchTypeParams {
  pageSize?: number;
  query: string;
  type: SearchType;
  skip?: boolean;
  excludeIds?: string[];
  additionalQueryFilters?: SearchParams['query'];
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

type SearchTypeParamsWithPage = SearchTypeParams & {
  page: number;
};

const buildSearchParams = ({
  page,
  pageSize = 10,
  query,
  type,
  excludeIds,
  additionalQueryFilters,
}: SearchTypeParamsWithPage): SearchParams => {
  const capitalizedType = capitalize(type);

  const queryParams: SearchParams['query'] = [
    query && {
      id: 'search_bar',
      operator: 'simple_text_search',
      type: 'filter',
      value: { query },
    },
    ...(additionalQueryFilters ?? []),
  ].filter(Boolean);

  if (excludeIds?.length) {
    queryParams.push({
      type: 'operator',
      value: 'and',
    });
    queryParams.push({
      type: 'filter',
      id: `${type}_id`,
      operator: 'not_contains_any',
      value: {
        value_list: excludeIds,
      },
    });
  }

  return {
    page,
    pageSize,
    query: queryParams,
    sortBy: {
      id: `name_${capitalizedType}`,
      desc: false,
    },
    type,
    filters: [
      `name_${capitalizedType}`,
      'document_current_version_name_Ticket',
      'file_type_Document',
      'stage_Ticket',
    ],
  };
};

const fetchSearch = async (params: SearchTypeParamsWithPage) => {
  const searchParams = buildSearchParams(params);
  const { results, meta } = await search(searchParams);
  const flattenedResults = results?.map((result) => ({
    id: result.id,
    // Since we are merging 2 independent searches, we need to maintain a stable sort order for the results.
    // otherwise tickets always get added at the end.
    sort: `${params.page}-${params.type}-${result.id}`,
    ...flattenSearchSelectedItemValues(result.selected_field_values),
  }));

  return {
    results: flattenedResults,
    hasMore: !!meta?.page?.next,
  } as SearchForTypeResultType;
};

export type UseSearchForTypeResultFields =
  | 'name'
  | 'id'
  | 'fileType'
  | 'documentCurrentVersionName';
export type UseSearchForTypeResult = Record<
  UseSearchForTypeResultFields | 'sort',
  string
>;

export interface SearchForTypeResultType {
  results: UseSearchForTypeResult[];
  hasMore: boolean;
}

/*
 * A hook to reduce boilerplate when searching for documents or tickets.
 */
export const useSearchType = ({
  pageSize = 10,
  query,
  type,
  skip = false,
  excludeIds,
  additionalQueryFilters,
}: SearchTypeParams) => {
  return useLoadMore<UseSearchForTypeResult>({
    fetchFn: async ({ page }) => {
      try {
        return await fetchSearch({
          page,
          pageSize,
          query,
          type,
          excludeIds,
          additionalQueryFilters,
        });
      } catch {
        return {
          results: [],
          hasMore: false,
        };
      }
    },
    skip: skip || !query,
    resetDeps: [query, type, excludeIds],
  });
};
